define((require) => {
    "use strict";

    const _               = require("underscore");
    const Class           = require("shared/util/Class");
    const HttpStatusCodes = require("shared/util/HttpStatusCodes");
    const Promise         = require("shared/util/Promise");
    const Permissions     = require("shared/security/Permissions");
    const ErrorHandler    = require("shared/services/error/ErrorHandler");
    const LoggedIn        = require("shared/security/LoggedIn");
    const {Logger}          = require("shared/util/Logger");

    const log = new Logger("Syncer");

    /**
     * @type {String}
     * Value rejected by any promise returned by <tt>invoke(..)</tt> for this syncer if the sync request
     * was cancelled for whatever reason, e.g. manually or because of timeout.
     */
    const CANCELLED = "cancelled";

    /**
     * @class {State}
     * Enum of all sync states. <p>
     *
     * Publicly exposed as <tt>Syncer.State</tt>.
     */
    const State = {
        Unsynced: "unsynced",
        Pending:  "pending",
        Syncing:  "syncing",
        Synced:   "synced",
        Failed:   "failed",
        Cancel:   "cancel",

        /**
         * Returns true if the current sync state of the specified target is complete, i.e. not syncing or pending.
         *
         * @param {*} target The target to test; cannot be falsey.
         * @return {Boolean} True if complete, false otherwise.
         */
        complete(target) {
            const state = State.current(target);
            return state !== State.Syncing && state !== State.Pending;
        },

        /**
         * Any synced target will have an augmented <tt>syncState</tt> property via this function, storing the state
         * for the <tt>current</tt> sync state (i.e. what is going on now during an active (pending) sync), and an
         * <tt>actual</tt> state (i.e. what was the last completed sync state). This is why targets must always be a
         * mutable object so we can assign said <tt>syncState</tt> property. <p>
         *
         * This function is invoked for any target tried synced, thus creating a <tt>syncState</tt> property for
         * the target, if not already. <p>
         *
         * The <tt>syncState</tt> object has the following format:
         * <pre>
         *   {
         *      actual: {             // actual completed target sync state
         *          state:  State,    // actual sync state for the target (typically State.Synced or State.Failed)
         *          method: String,   // actual sync method, e.g. "save"
         *          error:  *         // actual (first) error on State.Failed
         *      },
         *      current: {            // current target sync state (i.e. while ongoing sync)
         *           state:  State,   // current sync state, any constant from State
         *           method: String,  // sync method, e.g. "save"
         *           error:  *        // current error on State.Failed
         *      },
         *      immutable: Boolean,   // whether or not the target is immutable (default no); if so, all mutable requests will be dropped
         *      unique: Function|[],  // a function to return array of globally unique ids for the synced target,
         *                            // or an array of such unique properties on target; will default to
         *                            // target *instance* itself.
         *      alias:  {},           // user-friendly aliases map for better notifications, e.g. "save" -> "share" for Share models
         *      time:   Date.now(),   // latest sync time, for stats
         *      synced: [
         *          {
         *              method: String, // successful sync method
         *              time:   Number  // successful time of sync
         *          },
         *          ..
         *      ],
         *      failed: [
         *          {
         *              method: String,  // failed sync method
         *              time:   Number,  // time of failed sync
         *              id:     String   // notification id, if any, of the notification shown because of the failure,
         *                               // to be discarded on next actual successful success.
         *          },
         *           ..
         *      ]
         *   }
         * </pre>
         *
         * For both <tt>actual</tt> and <tt>current</tt>, each individual sync state can be:
         * <ul>
         * <li> <tt>State.Unsynced</tt> ("unsynced"): Initial state, not synced yet.
         * <li> <tt>State.Pending</tt> ("pending"):   Changes detected, but syncing is debounced a bit to check if more
         *                                            changes are coming before actually invoking the sync; used to avoid
         *                                            flooding the server with sync request for each small change.
         * <li> <tt>State.Syncing</tt> ("syncing"):   While a sync request (e.g. to the backend) is ongoing, e.g. request
         *                                            submitted for "save" or "destroy", but no response yet.
         * <li> <tt>State.Synced</tt> ("synced"):     deferred (request) succeeded, e.g. successful save.
         * <li> <tt>State.Failed</tt> ("failed"):     Syncing failed, e.g. because of a http error or some JS (uncaught)
         *                                            error.
         * </ul>
         *
         * In addition, <tt>current</tt> can have a state of <tt>State.Cancel</tt> ("cancel") if the a cancel is made
         * while a sync request was not actually syncing (yet). Cancellation thus only makes sense for pending
         * sync requests. <p>
         *
         * The <tt>actual</tt> state will be updated once the <tt>current</tt> state changes, but only where relevant.
         * For example, <tt>actual</tt> will not be set to "syncing" during save if it already has a state of "synced"
         * or "failed", in order to track the actual state even while a save is ongoing. Also, the first actual
         * <tt>error</tt> encountered will never be overridden until a save succeeds (and perhaps a later one
         * fails again). <p>
         *
         * For both <tt>active</tt> and <tt>current</tt>, <tt>method</tt> is the logical type of syncing performed,
         * defaulting to "save". Can be "fetch", "save", and "destroy", but is also used by sub-classes for additional
         * sync types, such as "push" for <tt>ExperienceModel</tt> that is also known to update/save the model. <p>
         *
         * During syncing, the <tt>sync:state</tt> event will be triggered accordingly, e.g. <tt>sync:state</tt>
         * will be triggered multiple times during a save. During these events, listeners can check the state of the
         * model in question via <tt>model.syncState.current<p>, or by the supplied listener arguments
         * (state, method, error), in that order. <p>
         *
         * Backbones <tt>sync</tt> event is raised <b>after</b> <tt>sync:state</tt> "synced".
         *
         * The <tt>syncState</tt> is considered read-only, only updated by the sync manager.
         *
         * @param {*}  target   The target to initialise with the sync state; cannot be falsey.
         * @param {*} [options] Optional options to register, e.g. <tt>{alias: {destroy: "delete"}}</tt>.
         *
         * @return {*} The sync state (not target); never falsey.
         */
        init(target, options) {
            let syncState = target.syncState;
            // Initialise sync state for the target instance if first time synced (at all)...
            if (!syncState) {
                syncState = {
                    actual:    {
                        state:  State.Unsynced,
                        method: "",
                        error:  null
                    },
                    current:   {
                        state:  State.Unsynced,
                        method: "",
                        error:  null
                    },
                    immutable: false,
                    unique:    [],
                    alias:     {}, // user-friendly aliases for better notifications
                    time:      Date.now() // stats
                };
            }

            if (options) {
                _.extend(syncState, options); // we assume we play nice...
            }

            // Stats; we only ever initialise error array on first error since only for stats...
            if (!syncState.synced) {
                syncState.synced = [];
            }

            target.syncState = syncState;

            return syncState;
        },

        /**
         * Returns the <tt>syncState</tt> object for the supplied target, creating it if need be (and not the
         * specific current state).
         *
         * @param {*} target The target to get the sync state object for; cannot be falsey.
         * @return {*} The sync state object for <tt>target</tt>; never falsey.
         */
        state(target) {
            if (!target.syncState) {
                State.init(target);
            }
            return target.syncState;
        },

        /**
         * Returns the current state of the supplied target.
         *
         * @param {*} target The target to get the current sync state for; cannot be falsey.
         * @return {State} The current sync state for <tt>target</tt>, defaulting to <tt>State.Unsynced</tt> if
         *                 no sync state is found for <tt>target</tt>.
         */
        current(target) {
            return State.state(target).current.state;
        }
    };

    /**
     * @class {Syncer}
     * A <i>syncer</i> performs the actual sync with some (backend) endpoint for a given target in a defacto
     * sequential manner as it is used by a target queue, which guarantees just that per target instance. It
     * also ensures that any sync method will timeout after a configured time if not already completed. <p>
     *
     * The syncing is performed via deferreds, typically - but not required to - connecting to some backend
     * endpoint to de some remote work described by, and perhaps to be reflected in, the given target. <p>
     *
     * Any target synced by a syncer will be augmented with a <tt>syncState</tt> property that stores the
     * sync state for that specific target. It will be assigned automatically when first synced, or manually
     * via <tt>State.init(target)</tt>. When the sync state changes for the target, the
     * "sync:" + method + ":" + state event will be triggered for the target, if and only if the target
     * supports a <tt>trigger</tt> method, like models or views.
     *
     * @author Gunni Rode / <a href="http://www.zmags.com">Zmags</a>
     *
     * @see    State#init
     * @see    TargetQueue
     */
    return Class.extend("Syncer", {

        /**
         * @type {SyncManager}
         * The sync manager that created this syncer; never falsey.
         */
        manager: null,
        /**
         * @type {*}
         * The target being synced; never falsey.
         */
        target: null,
        /**
         * @private @type {Number}
         * The timeout in ms for sync requests.
         */
        _timeout: null,
        /**
         * @protected @type {Number}
         * Nested invocation count, e.g. if a (de-)queued requests queues another request. Used to avoid
         * deadlocks in such situations.
         */
        _nested: null,

        /**
         * @constructor
         * Constructor, which creates this syncer.
         *
         * @param {SyncManager} manager   The sync manager that uses this syncer; never falsey.
         * @param {*}           target    The target to sync; never falsey.
         * @param {Number}      [timeout] The timeout for sync requests, defaults to 60s.
         */
        constructor(manager, target, timeout) {
            this.manager  = manager;
            this.target   = target;
            this._timeout = timeout || 60000; // 60s
            this._nested  = 0;

            // Make sure we have a "syncState" property, if not already, on the (to be) synced target...
            State.state(this.target);
        },

        /**
         * Returns the <tt>syncState</tt> object for the target synced by this syncer.
         *
         * @return {State} The sync state object for the target synced by this syncer; never falsey.
         * @see    State#state
         */
        syncState() {
            return this.target.syncState;
        },

        /**
         * Returns the current globally unique target identifiers for the target synced by this syncer.
         *
         * @return {[]} The current globally unique target identifiers; never falsey, and will always contain
         *              at the least the synced target.
         * @see    SyncManager#targetIdentifiers
         */
        targetIdentifiers() {
            return this.manager.targetIdentifiers(this.target);
        },

        /**
         * @protected
         * Tags the target as having the specified sync request as pending, to be executed (or cancelled) sometime in
         * the near future. If such a sync method is already in progress, the state is not changed, i.e. we do not
         * go from "save:syncing" (or any other sync method for that matter) to "save:pending" without the current sync
         * to complete first. <p>
         *
         * The actual sync request will already have been queued in the proper queue, blocking it until the pending
         * delay is reached. <p>
         *
         * Do not call, used by <tt>TargetQueue</tt> to signal pending requests via this syncer.
         *
         * @param {*} request The pending request for sync method, e.g. for a pending "save".
         *
         * @see   #cancel
         * @see   #invoke
         */
        _pending(request) {
            if (State.complete(this.target)) {
                this._state(request, State.Pending, false); // will not re-trigger pending
            } else {
                log.info(`Ignoring pending state, sync in progress: ${request} -> ${this}`, this);
            }
        },

        /**
         * @private
         * Validates the supplied request before invoking the actual sync request, returning the error to reject
         * the sync request with, or a falsey value (null) to indicate no error.
         *
         * Returning <tt>State.Cancel</tt> will cancel the request before an actual sync is performed, akin to
         * a cancellation having been made for the request after queue time. <p>
         *
         * A principal must be logged in, or the request will be cancelled. In addition, if a privilege or role
         * has been specified for the request, the principal will be validated against it, returning 403 if
         * insufficient privileges.
         *
         * @param {*}  request The sync request to validate; never null.
         * @return {*} The validation error as any non-null value, <tt>State.Cancel</tt> for cancellation,
         *             including if no logged in principal, or null for no error.
         */
        _validate(request) {
            // Make sure the logged in principal has access to the sync method in question if a permission has
            // be specified on the method object. A principal must always be present, e.g. we will drop all
            // sync requests after the principal has logged out, for example...
            let error = null; // null = no error!
            const principal = LoggedIn.principal();
            const permission = Permissions.Privileges.of(request.privilege) || Permissions.Roles.of(request.role);

            // Cancel, since no principal (e.g. on log out)...
            if (!principal) {
                return State.Cancel;
            } else if (permission && permission.cannot(principal)) {
                error = HttpStatusCodes.FORBIDDEN; // permission denied!
            } else if (request.validate) {
                try {
                    error = request.validate.call(this.target);
                    if (typeof error === "undefined") {
                        error = null; // no error!
                    }
                } catch (e) {
                    error = e;
                }
            }
            // Log error here for clarity...
            if (error !== null) {
                log.error(`Validation error for: ${request} -> ${error}`, request, error);
                // On http, we assume invalid submitted data since the validation failed...
                if (_.isString(error) && request.http) {
                    error = HttpStatusCodes.BAD_REQUEST; // bad request
                }
            }
            return error;
        },

        /**
         * Creates a new deferred for the supplied request. <p>
         *
         * A factory method like this is needed to ensure we can cancel (reject) a queued request that has not been
         * invoked, which must trigger target sync state correctly.
         *
         * @param {*} request The sync request about to be enqueued, to be invoked at a later time; never falsey.
         * @return {$.Deferred} The deferred to handle the invocation of <tt>request</tt>; never null.
         */
        _deferred(request) {
            return Promise.deferred()
                // Synced handler...
                .done(() => {
                    this._state(request, State.Synced);
                })

                // Fail/Cancelled handler...
                .fail((...args) => {
                    // For http requests, the first argument is the xhr request with its "status" property used
                    // to deduce the error; otherwise the first argument is assumed to be the error. However,
                    // if deferreds are "manually rejected" for xhr requests, like in the font loader if linked
                    // fonts are not supported, we might not even get an xhr request as we would otherwise do
                    // had the actual (http) request failed. In lack of a better status code, we use 501 - Not
                    // Implemented...
                    let error = args[0] || (request.http ? {status: 501} : {});
                    let state = State.Failed;
                    let silent = false;

                    if (error === CANCELLED) {
                        state  = State.Cancel;
                        silent = args[1];
                        error  = null; // cancelled is not an error as such
                    }

                    // On error, "syncState.current.error" will be set to the current error...
                    this._state(request, state, silent, error); // "error" ignored on "cancel", i.e. CANCELLED

                    // Do we have an undo handler...?
                    if (request.undoHandler) {
                        // Actual error...
                        error = this.syncState.current.error;
                        try {
                            request.undoHandler(error, request);
                            log.info(`Undo handler called for: ${request}`, request);
                        } catch (e) {
                            log.error(`Undo handler failed: ${request} -> ${error}`, request, error);
                            // Fall-through...
                        }
                    }
                });
        },

        /**
         * Invokes the specified sync request on the target handled by this syncer, notifying the deferred associated
         * with request on completion (success or failure).
         *
         * @param  {*}       request The sync request to invoke (created via <tt>queue.request(..)</tt>); never falsey.
         * @return {Promise} The internal promise used by this syncer, <b>not</b> a publicly exposed one (which can
         *                   be obtained via <tt>request.promise()</tt>. One nested enqueuing, the returned promise
         *                   will reveal its queue via the <tt>queue</tt> property.
         *
         * @throws Error If the target synced does not have a complete sync state when this function is invoked, e.g.
         *               if a sync is already in progress.
         *
         * @see    #pending
         * @see    #cancel
         */
        invoke(request) {
            const deferred = request.deferred(); // deferred used publicly to hand out promises
            const target   = this.target;
            let promise  = null; // sync method promise, *not* a promise from "deferred"!
            let error    = this._validate(request); // make sure request is valid!

            // Have we been cancelled while we were pending, either explicitly or because the validation returns
            // State.Cancel? This can be the case on debounced save or because the affect of syncing is no
            // longer needed, or in case of logout, for example...
            if (error === State.Cancel || State.current(target) === State.Cancel) {
                error = CANCELLED;
            }

            // Signal that we are now syncing for "target"....
            if (error === null) {
                // Sync method promise, not the promise given by this syncer. That is, this promise typically
                // describes some http(s) call...
                try {
                    if (!(this._nested++)) {
                        this._state(request, State.Syncing);
                    }

                    // On nested queuing, the return value is a promise, tagged with the (this) queue in
                    // the "queue" property, i.e. "promise.queue". On such cases, we cannot simply resolve our
                    // request/deferred to allow the nested sync request to be dequeued as that would trigger
                    // context listeners about the top level requests is finished, while its actual sync method
                    // is actually still pending (i.e. the nested sync request). This is why we have to handle
                    // this on nested enqueue time (where we simply share the top level deferred)...
                    promise = request.invoke();

                // On error, we (may not) have a promise either, so we also create one there...
                } catch (e) {
                    error = e;
                    log.error(`Sync method raised error: ${request}`, target, e);
                    // Fall-through!
                } finally {
                    this._nested--;
                }
            }

            // In certain cases, Backbone will never do a server round trip, such as when deleting/destroying an
            // unsaved model. If so, Backbone sync methods do not return promises, but the boolean value false.
            // Other user defined sync methods may also return non-promise values. If so, we create an already
            // resolved/rejected promise to use here, which will trigger the corresponding sync state immediately...
            if (!Promise.isPromise(promise)) {
                promise = error !== null ? Promise.rejected(error) : Promise.resolved(promise || target); // "promise" is return value from "request.invoke(..)" here
            }

            // We only need to handle timeout if the sync request promise is still pending, e.g.
            // doing a asynchronous http request. On timeout, "deferred" will be rejected, and its fail handler
            // called (set up in "_deferred(request)" function before this function was called)...
            if (Promise.isPending(promise)) {
                Promise.time(deferred, promise, this._timeout, HttpStatusCodes.REQUEST_TIMEOUT);
            } else {
                log.verbose("Promise already", error ? "rejected" : "resolved", `for: ${request}`, promise, this);
            }

            // Make sure the backing deferred is resolved once the sync request promise is complete and that we
            // single property sync:state events. Note these callbacks can arrive after we have resolved "deferred",
            // e.g. on timeout. If "promise" is not a cancelable promise, the work done in its "done/fail/always"
            // handlers will still be called, which may trigger strange side effects based, like cascading updates and
            // events fired. However, "deferred" will have completed in such cases, and it will not be resolved again,
            // i.e. the "deferred.done/fail" set above will not be triggered...
            return promise
                .done((...args) => {
                    let applyArgs = args;

                    // Resolve, either with values as is, or by transforming incoming values to some result format...
                    if (_.isFunction(request.resultHandler)) {
                        // Add request and target as extra arguments...
                        applyArgs.push(request);
                        applyArgs.push(target);
                        try {
                            // Always make this an array as to respect a returned array as well...
                            applyArgs = [request.resultHandler.apply(target, applyArgs)];

                        // Make sure the result handler does not mess the internal state up on unexpected error...
                        } catch (e) {
                            deferred.rejectWith(deferred, [e]); // internal error, should never happen!
                            return;
                        }
                    }
                    deferred.resolveWith(deferred, applyArgs); // will trigger deferred.done set up in "_deferred(request)"!
                })
                .fail((...args) => {
                    deferred.rejectWith(deferred, args); // will trigger deferred.fail set up in "_deferred(request)"!
                });
        },

        /**
         * Returns a new promise in place of <tt>promise</tt> that will only fire <tt>done</tt> handlers on
         * <tt>promise</tt> if the target used by this syncer has not been cancelled while <tt>promise</tt>
         * was executing. If cancelled, <tt>promise</tt> (and the returned promise) will be rejected with
         * a value of <tt>CANCELLED</tt>. <p>
         *
         * This function is expected to be called from within an actual sync function performing some form of
         * physical updates on the target, e.g. "save", to mask the promise returned by the actual sync method.
         * That is, a request must be pending or it does not make sense to mask <tt>promise</tt>. In addition,
         * the current request must be tagged as cancelable. If both these requirements are not met,
         * <tt>promise</tt> is simply returned by this function. <p>
         *
         * Nested sync requests are backed by the same deferred,
         *
         * Sync methods that do not update a target, e.g. "fetch", can use this function, but it is really not
         * needed.
         *
         * @param  {Promise|Boolean} promise  The promise to intercept, or a boolean false value in case Backbone
         *                                    will not do a server round trip, like when trying to destroy an unsaved
         *                                    model. In the latter case, this function will just return an already
         *                                    resolved promise that will resolved with the target used by this
         *                                    syncer.
         * @param  {*}               [nested] The nested request actually executing, if any and if known; can be
         *                                    falsey. Only used when nested.
         * @return {Promise} The (new) promise to use in place <tt>promise</tt>; never falsey.
         */
        cancelable(promise, nested) {
            if (!promise) {
                return Promise.resolved(this.target);
            }

            // Expected to be called during sync, so nested will always be 1 on normal requests, and thus
            // something larger than 1 on nested requests...
            const current = this.target.syncState.current;
            const parent  = current.request;
            const request = this._nested > 1 ? (nested || parent) : parent;

            // No (parent) request means this is function is not called from within actual sync logic, so we can never
            // cancel it. We cannot cancel requests already issued known to for example update model version
            // numbers...
            if (!parent || !request.cancelable.call(this.target)) { // TODO: should we eventually check both requests?
                return promise;
            }

            // Since this is expected to be executed before actual done handlers for "promise", e.g. to update
            // a model with a server response, we know that we have not changed the sync state of the target
            // in question yet, so it must still be syncing - unless it has been cancelled in the meantime. We
            // cannot cancel a different sync request, so we ignore such situations by returning true here.
            // On nested sync requests (promises), the "sync:state" of target will only change once the top level
            // request is done, i.e. when the "parent" request is done...
            return Promise.predicate(promise, {
                predicate() { // never called on "fail"!
                    return current.request !== parent || current.state !== State.Cancel;
                },
                reject: CANCELLED
            });
        },

        /**
         * Cancels the supplied sync request by rejecting its backing deferred with a value of <tt>CANCELLED</tt>. <p>
         *
         * Already executing sync requests cannot be cancelled as they might be doing all kinds of work on some
         * server elsewhere, i.e. the actual effect in the executing sync request cannot be cancelled. However, it
         * is possible to reject the currently executing promise, returned by <tt>invoke(..)</tt>, which will cause a
         * "sync:" + current.method + ":failed" event to be raised.
         *
         * @param  {*} request The sync request to cancel; cannot be falsey.
         * @return {Boolean} True if cancelled "now", false otherwise (i.e. if the backing deferred is not pending).
         *
         * @see   #invoke
         */
        cancel(request, silent) {
            const deferred = request.deferred();
            if (Promise.isPending(deferred)) {
                deferred.rejectWith(deferred, [CANCELLED, silent]);
                return true;
            }
            return false;
        },

        /**
         * @private
         * Sets the new state of the target handled by this syncer to the supplied state if and only if the new
         * state is applicable.
         *
         * @param {String|*} request  The request causing the state change, or just the sync method name, e.g. "save"
         *                            (never a private name like "_save") on "pending" or "cancel"; never falsey.
         * @param {String}   state    The new state, as a constant from <tt>State</tt>; never falsey.
         * @param {Boolean}  [silent] True if silent, false otherwise (default). If silent, no event will be triggered.
         * @param {Error|*}  [error]  Optional error or error value in case of <tt>State.Failed</tt>; can be falsey.
         *
         * @return {Boolean} True if the current sync for the handled target was cancelled because of the state
         *                   change, false otherwise.
         */
        _state(request, state, silent, error) {
            silent = silent || request.silent;
            try {
                const syncState = this.target.syncState;
                const method    = request.method || request; // i.e. when "request" is just a method name for "pending" or "cancel"
                const now       = Date.now();
                const current   = syncState.current; // current state, changed here
                const actual    = syncState.actual;  // actual state, only changed on completion here
                const changed   = current.state !== state;
                const trigger   = !silent && changed && _.isFunction(this.target.trigger); // only trigger when supported, not silent, and on change
                let cancelled = current.state === State.Cancel; // cancelled already? If so, we do not cancel again...

                // A new cancellation? Will ever only cancel a sync request if currently "pending" or possibly if
                // "syncing" as well...
                if (state === State.Cancel) {
                    // If a sync is already in progress, e.g. calling some server, we cannot cancel and must ignore the
                    // cancellation request - the backend is probably busy doing stuff. We cannot even cancel on fetch, as
                    // we do not know which callback hooks are waiting for the result. We check for pending, since a
                    // cancellation means nothing for states prior to sync, i.e. "unsynced", or post to sync, i.e. "synced"
                    // or "failed"...
                    if (current.state !== State.Pending && current.state !== State.Syncing) {
                        return false; // continue as this call never happened, ignore cancellation!
                    }
                    cancelled = true;
                }

                // Update current sync state...
                current.state   = state;
                current.method  = method;
                current.error   = undefined;
                current.request = null;

                // Unfortunately, we have to use a timer for each time a sync starts, to ensure that unhandled errors
                // will still be reflected in the target sync state, e.g. if a listener on "sync:state", for example,
                // raises an unhandled error, done() and fail() handlers will *not* be called. The timer will have been
                // started by "invoke(deferred, request)"...
                if (state === State.Syncing) {
                    // Have we been cancelled already? Then make sure the state matches the "actual" state and return...
                    if (cancelled) {
                        current.state   = actual.state;
                        current.method  = actual.method;
                        current.error   = actual.error;
                        return true; // signal cancelled!
                    }
                    // Tag with syncing request...
                    current.request = request;
                }

                // Update actual state if the state indicates the sync has completed, successfully or not...
                if (state === State.Synced || state === State.Unsynced) {
                    actual.method = method;
                    actual.state  = state;
                    actual.error  = undefined;

                    syncState.synced.push({method, time: now}); // stats, for debug

                    // Discard error messages since we are now synced successfully (again) unless silent...
                    if (!silent) {
                        _.each(syncState.failed, (entry) => {
                            if (entry.id) {
                                ErrorHandler.discard(entry.id);
                            }
                        });
                    }
                    delete syncState.failed;

                // Preserve the first error for consistency as the actual error!
                } else if (state === State.Failed) {
                    // Resolve http(s) status code into error, which is not resolved completely yet, or make sure we
                    // always have an error to avoid odd cascading errors from error handling on an unexpectedly
                    // undefined error...
                    if (request.http) {
                        error = ErrorHandler.fromHttp(error);
                    } else if (typeof error === "undefined") {
                        error = "unknown";
                    }

                    if (_.isString(error)) {
                        error = {message: error};
                    }

                    // Alert error if so the specified handler says so; this will also format the actual
                    // "current.error" with a properly formatted error message, if not already. We save
                    // a reference to the returned id(s), so we can close reported errors if the syncing
                    // later succeeds...
                    const id = !silent && ErrorHandler.fail(error, request.errorHandler || {method});

                    // Assign current error...
                    current.error = error;

                    actual.state = state;

                    // ...but only store first actual error...
                    if (!actual.error) {
                        actual.method = method;
                        actual.error  = current.error;
                    }

                    if (!syncState.failed) { // stats
                        syncState.failed = [];
                    }
                    syncState.failed.push({method, time: now, id});
                }

                // Trigger on changed state only (and when not specified to be silent)...
                if (changed) {
                    log.info(`Sync state ${(silent ? "silent " : "")} change after`, now - syncState.time, `ms: ${request}:${state}`, `->  ${this.target}`, this.target);
                    if (error) {
                        log.error(error);
                    }
                    syncState.time = now;
                    // So we still log and set meta data, but only trigger if we have a trigger function and unless
                    // not silent...
                    if (trigger) {
                        // Supplied target as last argument since it will otherwise not be available when target is
                        // re-triggered via a containing Backbone collection...
                        this.target.trigger("sync:state", state, method, error, this.target);
                    }
                }
                return cancelled;

            // Make sure unexpected exceptions will not interfere...
            } catch (e) {
                log.error("Unexpected error: {}", e);
                return false;
            }
        },

        /**
         * Returns the string representation of this syncer.
         *
         * @return {String} The string representation of this syncer; never falsey.
         */
        toString() {
            return `Syncer[identifiers: [${this.targetIdentifiers().join(", ")}]; timeout: ${this._timeout}ms]`;
        }

    }, {

        State,

        /** Value rejected by this syncer on sync cancellation. */
        CANCELLED
    });
});



// WEBPACK FOOTER //
// ./src/shared/services/sync/Syncer.js